/**
 * 🔒 VAULT SEAL — DO NOT MODIFY (Feb 24 2026). See /app/memory/VAULT_SEAL.md.
 * Sealed until the user types "Upgrade Vault".
 */

import React, { useState } from 'react';
import { Lock, Sparkles, Copy, Check, X, KeyRound, ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

/**
 * Two-step admin modal:
 *   1. Enter (or auto-generate) a vault code → submit
 *   2. Show the granted code ONCE in a copyable block with a strong
 *      "save this safely — you will need it every time" warning.
 *
 * Mirrors the offline-library grant flow but with a per-user secret.
 */
const GrantVaultModal = ({ user, onClose, onGranted }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [granted, setGranted] = useState(null); // { code, user_email }
  const [copied, setCopied] = useState(false);

  const generate = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let s = '';
    for (let i = 0; i < 8; i++) {
      s += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    setCode(s);
  };

  const submit = async () => {
    if (!code || code.length < 4) {
      setError('Code must be at least 4 characters.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await authFetch(
        `${BACKEND_URL}/api/admin/vault/grant/${user.user_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        },
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Failed to grant vault access.');
        return;
      }
      setGranted({ code: data.code, user_email: data.user_email });
      onGranted && onGranted();
    } catch (e) {
      setError('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(granted.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* noop */
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="grant-vault-modal"
    >
      <div className="w-full max-w-md bg-slate-900 border border-fuchsia-500/40 rounded-2xl shadow-[0_0_60px_-15px_rgba(217,70,239,0.45)] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-fuchsia-600/20 border border-fuchsia-500/40 flex items-center justify-center">
              <Lock className="w-4 h-4 text-fuchsia-300" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">
                {granted ? 'Vault code granted' : 'Grant Secret Vault'}
              </h3>
              <p className="text-xs text-gray-400">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            data-testid="grant-vault-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — step 1 */}
        {!granted && (
          <div className="px-5 py-5 space-y-4">
            <p className="text-sm text-gray-300 leading-relaxed">
              Type a code (4–64 chars) or click <span className="text-fuchsia-300 font-semibold">Generate</span>.
              The code will be shown <span className="text-amber-300 font-semibold">once</span> — copy and send it to the user.
              They must enter it every time they open the vault.
            </p>

            <div className="flex gap-2">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. OPEN1234"
                className="flex-1 px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono tracking-widest placeholder:text-gray-500 focus:outline-none focus:border-fuchsia-500"
                maxLength={64}
                data-testid="grant-vault-code-input"
              />
              <Button
                onClick={generate}
                variant="outline"
                className="text-fuchsia-300 border-fuchsia-500/50 hover:bg-fuchsia-600/20"
                data-testid="grant-vault-generate-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Generate
              </Button>
            </div>

            {error && (
              <p className="text-xs text-red-400 flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5" /> {error}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={onClose}
                variant="ghost"
                className="text-gray-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !code}
                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                data-testid="grant-vault-submit-btn"
              >
                <KeyRound className="w-4 h-4 mr-1.5" />
                {submitting ? 'Granting…' : 'Grant Access'}
              </Button>
            </div>
          </div>
        )}

        {/* Body — step 2 (success) */}
        {granted && (
          <div className="px-5 py-5 space-y-4">
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200 flex gap-2">
              <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
              <span>
                <span className="font-bold">Save this code safely.</span> Send it to {granted.user_email} —
                they will need it <span className="underline">every time</span> they enter the vault.
                We will not show it again.
              </span>
            </div>

            <div className="bg-slate-950 border border-fuchsia-500/40 rounded-xl p-4 text-center">
              <p className="text-xs uppercase tracking-wider text-fuchsia-300 mb-2">Vault code</p>
              <p
                className="text-3xl font-mono font-bold text-white tracking-[0.4em] select-all"
                data-testid="grant-vault-code-display"
              >
                {granted.code}
              </p>
              <button
                onClick={copy}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-fuchsia-300 hover:text-fuchsia-200 font-semibold"
                data-testid="grant-vault-copy-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" /> Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" /> Copy code
                  </>
                )}
              </button>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={onClose}
                className="bg-slate-700 hover:bg-slate-600 text-white"
                data-testid="grant-vault-done-btn"
              >
                I've saved it
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrantVaultModal;
